<template>
  <div class="card p-shadow-app">
    <h1>Encomendas e Peças</h1>
    <Toast />

    <TabView :activeIndex="activeTab" @tab-change="onTabChange">
      <!-- TAB 1: Peças de Encomendas a Cliente (por encomendar) -->
      <TabPanel header="Encomendas a Cliente">
        <div class="p-mb-3 p-d-flex p-jc-between p-ai-center flex-wrap" style="gap: 1rem;">
          <div class="p-d-flex p-gap-2" style="gap: 0.5rem;">
            <Button
              label="Nova Encomenda"
              icon="pi pi-plus"
              class="p-button-sm"
              @click="createNewClientOrder"
            />
            <Button
              label="Converter p/ Fornecedor"
              icon="pi pi-directions"
              class="p-button-sm p-button-success"
              :disabled="!selectedClientLines.length"
              :loading="convertLoading"
              @click="convertSelectedToSupplierOrder"
            />
            <Button
              label="Transferência de Armazém"
              icon="pi pi-sync"
              class="p-button-sm p-button-info"
              :disabled="!canConvertToWarehouseTransfer"
              :loading="convertLoading"
              @click="convertSelectedToWarehouseTransfer"
            />
          </div>
          <div class="p-text-secondary" v-if="selectedClientLines.length">
            {{ selectedClientLines.length }} peça(s) selecionada(s) ({{ selectedUniqueBostamps.length }} encomenda(s) a cliente).
            <span
              v-if="selectedUniqueBostamps.length > 1"
              style="color: #f59e0b; font-weight: 600; margin-left: 0.4rem;"
            >
              (Transferência de armazém disponível apenas para 1 encomenda a cliente)
            </span>
          </div>
        </div>

        <!-- Filters Section -->
        <div class="p-fluid p-formgrid p-grid p-mb-3">
          <div class="p-field p-col-12 p-md-2">
            <label for="clientOrderNo">Número da Encomenda</label>
            <InputText
              id="clientOrderNo"
              v-model="tab1Filters.obrano"
              placeholder="Filtrar por número..."
              @keyup.enter="fetchClientOrders"
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="clientProductRef">Referência da Peça</label>
            <InputText
              id="clientProductRef"
              v-model="tab1Filters.productRef"
              placeholder="Filtrar por referência..."
              @keyup.enter="fetchClientOrders"
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="clientDateFrom">Data Inicial</label>
            <Calendar
              id="clientDateFrom"
              v-model="tab1Filters.dateFrom"
              dateFormat="dd/mm/yy"
              placeholder="Data inicial"
              showIcon
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="clientDateTo">Data Final</label>
            <Calendar
              id="clientDateTo"
              v-model="tab1Filters.dateTo"
              dateFormat="dd/mm/yy"
              placeholder="Data final"
              showIcon
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="clientBrand">Marca</label>
            <InputText
              id="clientBrand"
              v-model="tab1Filters.brand"
              placeholder="Filtrar por marca..."
              @keyup.enter="fetchClientOrders"
            />
          </div>
          <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end" style="gap: 0.5rem;">
            <Button
              label="Filtrar"
              icon="pi pi-filter"
              class="p-button-secondary"
              @click="fetchClientOrders"
            />
            <Button
              label="Limpar"
              icon="pi pi-times"
              class="p-button-text p-button-secondary"
              @click="clearTab1Filters"
            />
          </div>
        </div>

        <DataTable
          :value="clientOrderLines"
          :loading="loadingTab1"
          :paginator="true"
          :rows="perPageTab1"
          :first="firstTab1"
          :rowsPerPageOptions="[10, 20, 50]"
          :lazy="true"
          :totalRecords="totalTab1"
          @page="fetchClientOrders"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Mostrar de {first} a {last} de {totalRecords} peças"
          :selection.sync="selectedClientLines"
          dataKey="id"
          responsiveLayout="scroll"
          class="p-datatable-sm"
          stripedRows
        >
          <template #empty>
            <div class="p-text-center p-p-4">
              <i class="pi pi-inbox" style="font-size: 2.5rem; color: #999;"></i>
              <p class="p-mt-2">Nenhuma peça pendente de encomenda a cliente encontrada</p>
            </div>
          </template>

          <Column selectionMode="multiple" style="width: 3rem" />

          <Column field="order_id" header="Nº Encomenda" style="min-width: 110px">
            <template #body="slotProps">
              <strong>#{{ slotProps.data.order_id }}</strong>
            </template>
          </Column>

          <Column field="pat_no" header="PAT" style="min-width: 100px">
            <template #body="slotProps">
              <span v-if="slotProps.data.pat_no">
                <router-link
                  :to="{ name: 'PatDetails', params: { id: slotProps.data.pat_no } }"
                  class="p-text-bold"
                >
                  <span class="clickable-item">{{ slotProps.data.pat_no }}</span>
                </router-link>
              </span>
              <span v-else class="p-text-secondary">-</span>
            </template>
          </Column>

          <Column field="client_name" header="Cliente" style="min-width: 160px" />
          <Column field="brand" header="Marca" style="min-width: 120px" />

          <Column field="date" header="Data" style="min-width: 100px">
            <template #body="slotProps">
              {{ formatDate(slotProps.data.date) }}
            </template>
          </Column>

          <Column field="product_ref" header="Referência" style="min-width: 120px">
            <template #body="slotProps">
              <code>{{ slotProps.data.product_ref }}</code>
            </template>
          </Column>

          <Column field="description" header="Descrição" style="min-width: 180px" />

          <Column field="quantity" header="Qtd." style="min-width: 80px; text-align: center">
            <template #body="slotProps">
              {{ slotProps.data.quantity }}
            </template>
          </Column>

          <Column field="sale_price" header="Preço Unit." style="min-width: 100px">
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.sale_price) }}
            </template>
          </Column>

          <Column field="total" header="Total" style="min-width: 100px">
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.total) }}
            </template>
          </Column>

          <Column header="Stock por Armazém" style="min-width: 200px">
            <template #body="slotProps">
              <div v-if="hasPositiveStock(slotProps.data.product_ref)">
                <span
                  v-for="st in stocksByReference[slotProps.data.product_ref]"
                  :key="st.id"
                  class="warehouse-stock-badge"
                  :class="st.stock > 0 ? 'stock-ok' : 'stock-empty'"
                >
                  {{ st.warehouse_name || st.warehouse }}: <strong>{{ st.stock }}</strong>
                </span>
              </div>
              <div v-else-if="loadingStocks[slotProps.data.product_ref]">
                <i class="pi pi-spin pi-spinner" style="font-size: 1rem; color: var(--primary-color)"></i>
              </div>
              <div v-else class="no-stock-label">
                <i class="pi pi-exclamation-circle"></i>
                não existe em stock
              </div>
            </template>
          </Column>

          <Column header="Ações" style="min-width: 80px">
            <template #body="slotProps">
              <Button
                icon="pi pi-pencil"
                class="p-button-rounded p-button-success p-button-text"
                v-tooltip.top="'Editar Encomenda Cliente'"
                @click="editDossier({ reference: slotProps.data.bostamp })"
              />
            </template>
          </Column>
        </DataTable>
      </TabPanel>

      <!-- TAB 2: Peças a Receber (Encomendas a Fornecedor) -->
      <TabPanel header="Peças a Receber">
        <div class="p-mb-3 p-d-flex p-jc-between p-ai-center flex-wrap" style="gap: 1rem;">
          <div class="p-d-flex p-gap-2">
            <Button
              label="Exportar CSV"
              icon="pi pi-file-excel"
              class="p-button-sm p-button-help"
              @click="exportToCSV"
              :loading="exportLoading"
              :disabled="loadingTab2 || exportLoading || !supplierOrders.length"
            />
          </div>
        </div>

        <!-- KPI Summary Cards -->
        <div class="p-grid p-mb-4" style="margin-top: 0.5rem; gap: 0.5rem; display: flex; flex-wrap: wrap;">
          <div class="p-col-12 p-md-4" style="flex: 1; min-width: 180px;">
            <div
              class="kpi-card p-p-3 p-shadow-1"
              style="background-color: var(--surface-e); border-left: 5px solid #f59e0b; border-radius: 4px;"
            >
              <span class="kpi-title">ENCOMENDAS FORNECEDOR</span>
              <span class="kpi-value" style="color: #f59e0b;">{{ summaryStats.total_orders || 0 }}</span>
            </div>
          </div>
          <div class="p-col-12 p-md-4" style="flex: 1; min-width: 180px;">
            <div
              class="kpi-card p-p-3 p-shadow-1"
              style="background-color: var(--surface-e); border-left: 5px solid var(--primary-color); border-radius: 4px;"
            >
              <span class="kpi-title">TOTAL PEÇAS</span>
              <span class="kpi-value" style="color: var(--primary-color);">{{ summaryStats.total_quantity || 0 }}</span>
            </div>
          </div>
          <div class="p-col-12 p-md-4" style="flex: 1; min-width: 180px;">
            <div
              class="kpi-card p-p-3 p-shadow-1"
              style="background-color: var(--surface-e); border-left: 5px solid #22c55e; border-radius: 4px;"
            >
              <span class="kpi-title">VALOR ESTIMADO</span>
              <span class="kpi-value" style="color: #22c55e;">{{ formatCurrency(summaryStats.total_value) }}</span>
            </div>
          </div>
        </div>

        <!-- Filters Section -->
        <div class="p-fluid p-formgrid p-grid p-mb-3">
          <div class="p-field p-col-12 p-md-2">
            <label for="supplierOrderNo">Nº Encomenda</label>
            <InputText
              id="supplierOrderNo"
              v-model="tab2Filters.obrano"
              placeholder="Nº encomenda..."
              @keyup.enter="fetchSupplierOrders"
            />
          </div>
          <div class="p-field p-col-12 p-md-3">
            <label for="partsSearch">Pesquisar</label>
            <InputText
              id="partsSearch"
              v-model="tab2Filters.search"
              placeholder="Fornecedor, referência, descrição ou POE..."
              @keyup.enter="fetchSupplierOrders"
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="partsDateFrom">Data Inicial</label>
            <Calendar
              id="partsDateFrom"
              v-model="tab2Filters.dateFrom"
              dateFormat="dd/mm/yy"
              placeholder="Data inicial"
              showIcon
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="partsDateTo">Data Final</label>
            <Calendar
              id="partsDateTo"
              v-model="tab2Filters.dateTo"
              dateFormat="dd/mm/yy"
              placeholder="Data final"
              showIcon
            />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="partsStatus">Estado</label>
            <Dropdown
              id="partsStatus"
              v-model="tab2Filters.closed"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Selecionar estado"
              @change="fetchSupplierOrders"
            />
          </div>
          <div class="p-field p-col-12 p-md-1 p-d-flex p-ai-end" style="gap: 0.4rem;">
            <Button
              icon="pi pi-filter"
              class="p-button-secondary"
              v-tooltip.top="'Filtrar'"
              @click="fetchSupplierOrders"
            />
            <Button
              icon="pi pi-times"
              class="p-button-text p-button-secondary"
              v-tooltip.top="'Limpar Filtros'"
              @click="clearTab2Filters"
            />
          </div>
        </div>

        <DataTable
          :value="supplierOrders"
          :loading="loadingTab2"
          :paginator="true"
          :rows="perPageTab2"
          :first="firstTab2"
          :rowsPerPageOptions="[10, 20, 50]"
          :lazy="true"
          :totalRecords="totalTab2"
          @page="fetchSupplierOrders"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Mostrar de {first} a {last} de {totalRecords} encomendas"
          dataKey="bostamp"
          responsiveLayout="scroll"
          class="p-datatable-sm"
          stripedRows
          :expandedRows.sync="expandedSupplierRows"
        >
          <template #empty>
            <div class="p-text-center p-p-4">
              <i class="pi pi-inbox" style="font-size: 2.5rem; color: #999;"></i>
              <p class="p-mt-2">Nenhuma encomenda a fornecedor encontrada para os filtros selecionados</p>
            </div>
          </template>

          <Column :expander="true" style="width: 3rem" />

          <Column field="id" header="Nº Encomenda" style="min-width: 110px">
            <template #body="slotProps">
              <strong>#{{ slotProps.data.id }}</strong>
            </template>
          </Column>

          <Column field="name" header="Fornecedor" style="min-width: 220px">
            <template #body="slotProps">
              {{ slotProps.data.name || '-' }}
            </template>
          </Column>

          <Column field="date" header="Data" style="min-width: 110px">
            <template #body="slotProps">
              {{ formatDate(slotProps.data.date) }}
            </template>
          </Column>

          <Column field="parts_quantity" header="Peças (Qtd.)" style="min-width: 110px; text-align: center">
            <template #body="slotProps">
              <Tag
                :value="`${slotProps.data.parts_count || 0} ref (${slotProps.data.parts_quantity || 0} un)`"
                severity="info"
              />
            </template>
          </Column>

          <Column field="observation" header="Observações" style="min-width: 220px">
            <template #body="slotProps">
              <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                <span
                  v-if="slotProps.data.observation"
                  class="p-text-truncate"
                  style="max-width: 200px; display: inline-block;"
                  v-tooltip.top="slotProps.data.observation"
                >
                  {{ slotProps.data.observation }}
                </span>
                <span v-else-if="!slotProps.data.lg_order_no" class="p-text-secondary">-</span>
              </div>
            </template>
          </Column>

          <Column field="closed" header="Estado" style="min-width: 120px">
            <template #body="slotProps">
              <Tag
                :value="slotProps.data.closed ? 'Recebido (Fechado)' : 'Por receber (Aberto)'"
                :severity="slotProps.data.closed ? 'success' : 'info'"
              />
            </template>
          </Column>

          <Column field="subtotal" header="Subtotal" style="min-width: 110px">
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.subtotal) }}
            </template>
          </Column>

          <Column field="total" header="Total (c/IVA)" style="min-width: 110px">
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.total) }}
            </template>
          </Column>

          <Column header="Ações" style="min-width: 180px">
            <template #body="slotProps">
              <div style="display: flex; align-items: center; gap: 0.4rem;">
                <Button
                  icon="pi pi-send"
                  label="Enviar p/"
                  class="p-button-sm p-button-warning"
                  v-tooltip.top="'Selecionar integração para envio da encomenda'"
                  @click="openIntegrationSelector(slotProps.data)"
                />
                <Button
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-success p-button-text"
                  v-tooltip.top="'Editar Encomenda a Fornecedor'"
                  @click="editDossier(slotProps.data)"
                />
              </div>
            </template>
          </Column>

          <template #expansion="slotProps">
            <div class="p-p-3" v-if="slotProps.data.lines && slotProps.data.lines.length">
              <DataTable :value="slotProps.data.lines" responsiveLayout="scroll" class="p-datatable-sm">
                <Column field="product_ref" header="Referência" style="min-width: 130px">
                  <template #body="lineProps">
                    <code>{{ lineProps.data.product_ref }}</code>
                  </template>
                </Column>
                <Column field="description" header="Descrição" style="min-width: 220px">
                  <template #body="lineProps">
                    {{ lineProps.data.description || '-' }}
                  </template>
                </Column>
                <Column field="quantity" header="Quantidade" style="min-width: 90px; text-align: center" />
                <Column field="cost_price" header="Preço Unit." style="min-width: 110px">
                  <template #body="lineProps">
                    {{ formatCurrency(lineProps.data.cost_price) }}
                  </template>
                </Column>
                <Column field="client_order_no" header="Enc. Cliente" style="min-width: 110px">
                  <template #body="lineProps">
                    <span v-if="lineProps.data.client_order_no">#{{ lineProps.data.client_order_no }}</span>
                    <span v-else class="p-text-secondary">-</span>
                  </template>
                </Column>
                <Column field="pat_no" header="PAT" style="min-width: 100px">
                  <template #body="lineProps">
                    <router-link
                      v-if="lineProps.data.pat_no"
                      :to="{ name: 'PatDetails', params: { id: lineProps.data.pat_no } }"
                      class="p-text-bold"
                    >
                      {{ lineProps.data.pat_no }}
                    </router-link>
                    <span v-else class="p-text-secondary">-</span>
                  </template>
                </Column>
                <Column field="service_receipt_no" header="RNU (LG)" style="min-width: 140px">
                  <template #body="lineProps">
                    <code v-if="lineProps.data.service_receipt_no">{{ lineProps.data.service_receipt_no }}</code>
                    <span v-else class="p-text-secondary">-</span>
                  </template>
                </Column>
                <Column field="warehouse_origin" header="Armazém" style="min-width: 140px">
                  <template #body="lineProps">
                    {{ lineProps.data.warehouse_origin_name || lineProps.data.warehouse_origin || '-' }}
                  </template>
                </Column>
              </DataTable>
            </div>
            <div v-else class="p-text-center p-p-3">
              <i class="pi pi-inbox" style="font-size: 2rem; color: #999;"></i>
              <p class="p-mt-2">Sem linhas de peças para exibir</p>
            </div>
          </template>
        </DataTable>
      </TabPanel>
    </TabView>

    <!-- POP-UP 1: Seleção de Integração / Sincronização -->
    <Dialog
      :visible.sync="integrationSelectorVisible"
      :header="
        integrationTargetOrder
          ? `Enviar Encomenda #${integrationTargetOrder.id} para...`
          : 'Selecionar Integração'
      "
      :modal="true"
      :style="{ width: '440px', maxWidth: '95vw' }"
    >
      <div class="p-fluid">
        <div class="p-field p-mb-2">
          <label for="integrationSelect">Integração / Sincronização</label>
          <Dropdown
            id="integrationSelect"
            v-model="selectedIntegration"
            :options="availableIntegrations"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione a integração..."
            appendTo="body"
          />
        </div>
        <small v-if="integrationTargetOrder" class="p-text-secondary">
          Fornecedor: <strong>{{ integrationTargetOrder.name }}</strong>
        </small>
      </div>

      <template #footer>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text p-button-secondary"
          @click="integrationSelectorVisible = false"
        />
        <Button
          label="Continuar"
          icon="pi pi-arrow-right"
          iconPos="right"
          class="p-button-warning"
          :disabled="!selectedIntegration"
          @click="proceedWithSelectedIntegration"
        />
      </template>
    </Dialog>

    <!-- POP-UP 2: Integração de Encomenda de Peças com a LG -->
    <Dialog
      :visible.sync="lgModalVisible"
      :header="lgModalData.obrano ? `Integração LG — Encomenda a Fornecedor #${lgModalData.obrano}` : 'Integração LG'"
      :modal="true"
      :style="{ width: '920px', maxWidth: '95vw' }"
      :closable="!lgSubmitting"
    >
      <div v-if="lgPreviewLoading" class="p-text-center p-p-5">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem; color: var(--primary-color)"></i>
        <p class="p-mt-2">A carregar dados da encomenda e moradas LG...</p>
      </div>

      <div v-else>
        <div
          v-if="lgModalData.existing_lg_order_no"
          class="p-mb-3 p-p-3"
          style="background: #f0fdf4; border-left: 4px solid #22c55e; border-radius: 4px;"
        >
          <i class="pi pi-check-circle p-mr-2" style="color: #16a34a;"></i>
          Esta encomenda já foi sincronizada anteriormente com a LG:
          <strong>{{ lgModalData.existing_lg_order_no }}</strong>
        </div>

        <div class="p-fluid p-formgrid p-grid p-mb-3">
          <div class="p-field p-col-12 p-md-4">
            <label for="lgCustomerOrderNo">Nº Encomenda (Customer Order No) *</label>
            <InputText
              id="lgCustomerOrderNo"
              v-model="lgModalData.customer_order_no"
              maxlength="50"
              placeholder="Ex: 94"
            />
          </div>

          <div class="p-field p-col-12 p-md-8">
            <label for="lgAddressRefNo">Morada de Entrega LG (Address Ref No)</label>
            <div style="display: flex; gap: 0.5rem;">
              <Dropdown
                v-if="lgModalData.addresses && lgModalData.addresses.length"
                id="lgAddressRefNo"
                v-model="lgModalData.address_ref_no"
                :options="lgModalData.addresses"
                optionLabel="label"
                optionValue="address_ref_no"
                editable
                placeholder="Selecionar ou introduzir código GSFS da morada..."
                style="flex: 1;"
                appendTo="body"
              />
              <InputText
                v-else
                id="lgAddressRefNo"
                v-model="lgModalData.address_ref_no"
                maxlength="30"
                placeholder="Código GSFS da morada (ex: AE0000005773357)"
              />
            </div>
          </div>

          <div class="p-field p-col-12">
            <label for="lgOrderRemark">Observação Global (Order Remark)</label>
            <InputText
              id="lgOrderRemark"
              v-model="lgModalData.order_remark"
              maxlength="256"
              placeholder="Observação opcional a enviar para a LG..."
            />
          </div>
        </div>

        <h4 class="p-mb-2">Peças a Enviar para a LG</h4>
        <DataTable
          :value="lgModalData.parts"
          :selection.sync="selectedLgParts"
          dataKey="bistamp"
          responsiveLayout="scroll"
          class="p-datatable-sm"
          stripedRows
        >
          <Column selectionMode="multiple" style="width: 3rem" />

          <Column field="part_no" header="Referência" style="min-width: 130px">
            <template #body="slotProps">
              <InputText v-model="slotProps.data.part_no" class="p-inputtext-sm" style="width: 100%;" />
            </template>
          </Column>

          <Column field="description" header="Descrição" style="min-width: 170px">
            <template #body="slotProps">
              <span>{{ slotProps.data.description }}</span>
              <div v-if="slotProps.data.pat_no" style="font-size: 0.75rem; color: #666;">
                PAT #{{ slotProps.data.pat_no }}
              </div>
            </template>
          </Column>

          <Column field="quantity" header="Qtd." style="width: 85px">
            <template #body="slotProps">
              <InputText
                v-model.number="slotProps.data.quantity"
                type="number"
                min="1"
                max="20"
                class="p-inputtext-sm"
                style="width: 70px; text-align: center;"
              />
            </template>
          </Column>

          <Column field="delivery_type" header="Tipo Entrega" style="min-width: 135px">
            <template #body="slotProps">
              <Dropdown
                v-model="slotProps.data.delivery_type"
                :options="lgDeliveryTypes"
                optionLabel="label"
                optionValue="value"
                class="p-inputtext-sm"
                style="width: 100%;"
                appendTo="body"
              />
            </template>
          </Column>

          <Column field="service_receipt_no" header="RNU (Service Receipt No)" style="min-width: 175px">
            <template #body="slotProps">
              <InputText
                v-model="slotProps.data.service_receipt_no"
                maxlength="20"
                placeholder="Ex: RNU..."
                class="p-inputtext-sm"
                style="width: 100%;"
              />
            </template>
          </Column>

          <Column field="order_line_comment" header="Observações" style="min-width: 150px">
            <template #body="slotProps">
              <InputText
                v-model="slotProps.data.order_line_comment"
                maxlength="2000"
                placeholder="Ex: Nº PAT..."
                class="p-inputtext-sm"
                style="width: 100%;"
              />
            </template>
          </Column>
        </DataTable>
      </div>

      <template #footer>
        <Button
          label="Voltar"
          icon="pi pi-arrow-left"
          class="p-button-text p-button-secondary"
          :disabled="lgSubmitting"
          @click="backToIntegrationSelector"
        />
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text p-button-secondary"
          :disabled="lgSubmitting"
          @click="lgModalVisible = false"
        />
        <Button
          label="Enviar Encomenda para LG"
          icon="pi pi-send"
          class="p-button-warning"
          :loading="lgSubmitting"
          :disabled="lgPreviewLoading || !selectedLgParts.length || !lgModalData.customer_order_no"
          @click="submitLgPartOrder"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import dossierService from '@/services/internalDossier.service';
import stockService from '@/services/stock.service';
import Toast from 'primevue/toast';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';

export default {
  name: 'InternalDossierOrderParts',
  components: {
    Toast,
    TabView,
    TabPanel,
    DataTable,
    Column,
    Button,
    Calendar,
    Dropdown,
    InputText,
    Tag,
    Dialog
  },
  data() {
    return {
      activeTab: 0,

      // Tab 1: Client Orders & Lines
      clientOrderLines: [],
      selectedClientLines: [],
      stocksByReference: {},
      loadingStocks: {},
      loadingTab1: false,
      perPageTab1: 50,
      firstTab1: 0,
      totalTab1: 0,
      tab1Filters: {
        obrano: '',
        productRef: '',
        brand: '',
        dateFrom: null,
        dateTo: null
      },
      convertLoading: false,

      // Tab 2: Peças a Receber (Encomendas a Fornecedor)
      supplierOrders: [],
      loadingTab2: false,
      expandedSupplierRows: [],
      perPageTab2: 50,
      firstTab2: 0,
      totalTab2: 0,
      supplierOrderParams: {},
      exportLoading: false,
      tab2Filters: {
        obrano: '',
        search: '',
        dateFrom: null,
        dateTo: null,
        closed: false
      },
      summaryStats: {
        total_orders: 0,
        total_quantity: 0,
        total_value: 0,
        unique_references: 0
      },
      statusOptions: [
        { label: 'Por receber (Aberto)', value: false },
        { label: 'Recebido (Fechado)', value: true },
        { label: 'Todos', value: null }
      ],

      // Integration Selector Pop-up
      integrationSelectorVisible: false,
      integrationTargetOrder: null,
      selectedIntegration: 'LG',
      availableIntegrations: [
        { label: 'LG (GSFS / XLiFT)', value: 'LG' }
      ],

      // LG Integration Pop-up
      lgModalVisible: false,
      lgPreviewLoading: false,
      lgSubmitting: false,
      selectedLgParts: [],
      lgModalData: {
        bostamp: '',
        obrano: null,
        supplier_name: '',
        customer_order_no: '',
        address_ref_no: '',
        order_remark: '',
        existing_lg_order_no: null,
        addresses: [],
        parts: []
      },
      lgDeliveryTypes: [
        { label: 'NORMAL', value: 'NORMAL' },
        { label: 'URGENT', value: 'URGENT' },
        { label: 'STOCK', value: 'STOCK' }
      ]
    };
  },
  computed: {
    selectedUniqueBostamps() {
      return [...new Set(this.selectedClientLines.map(line => line.bostamp))];
    },
    canConvertToWarehouseTransfer() {
      return this.selectedUniqueBostamps.length === 1;
    }
  },
  activated() {
    this.loadCurrentTab();
  },
  watch: {
    clientOrderLines: {
      immediate: true,
      handler(newVal) {
        if (!newVal || newVal.length === 0) {
          this.selectedClientLines = [];
        } else {
          const existingIds = new Set(newVal.map(item => item.id));
          this.selectedClientLines = this.selectedClientLines.filter(
            item => item && item.id && existingIds.has(item.id)
          );
        }
      }
    }
  },
  methods: {
    onTabChange(event) {
      this.activeTab = event.index;
      this.loadCurrentTab();
    },
    loadCurrentTab() {
      if (this.activeTab === 0) {
        this.fetchClientOrders();
      } else if (this.activeTab === 1) {
        this.fetchSupplierOrders();
      }
    },

    // ==========================================
    // TAB 1: CLIENT ORDERS (PEÇAS POR ENCOMENDAR)
    // ==========================================
    async fetchClientOrders(event = {}) {
      this.loadingTab1 = true;
      this.selectedClientLines = [];
      this.firstTab1 = event.first ?? 0;
      this.perPageTab1 = event.rows ?? this.perPageTab1;
      try {
        const params = {
          page: this.firstTab1 / this.perPageTab1 + 1,
          per_page: this.perPageTab1,
          obrano: this.tab1Filters.obrano || undefined,
          product_ref: this.tab1Filters.productRef || undefined,
          brand: this.tab1Filters.brand.trim() || undefined,
          date_from: this.tab1Filters.dateFrom ? this.formatDateForStorage(this.tab1Filters.dateFrom) : undefined,
          date_to: this.tab1Filters.dateTo ? this.formatDateForStorage(this.tab1Filters.dateTo) : undefined
        };
        const response = await dossierService.getClientOrderParts(params);
        this.clientOrderLines = response.data || [];
        this.totalTab1 = response.total;

        this.fetchStocksForLines();
      } catch (error) {
        this.showToast('error', 'Erro', 'Erro ao obter peças de encomendas a cliente.');
      } finally {
        this.loadingTab1 = false;
      }
    },
    fetchStocksForLines() {
      const refs = [...new Set(this.clientOrderLines.map(line => line.product_ref))];
      refs.forEach(ref => {
        if (!ref) return;
        if (this.stocksByReference[ref] === undefined && !this.loadingStocks[ref]) {
          this.$set(this.loadingStocks, ref, true);
          stockService
            .getWarehousesByReference(ref)
            .then(data => {
              this.$set(this.stocksByReference, ref, data || []);
              this.$set(this.loadingStocks, ref, false);
            })
            .catch(() => {
              this.$set(this.stocksByReference, ref, []);
              this.$set(this.loadingStocks, ref, false);
            });
        }
      });
    },
    hasPositiveStock(ref) {
      const stocks = this.stocksByReference[ref];
      return stocks && stocks.length && stocks.some(st => st.stock > 0);
    },
    clearTab1Filters() {
      this.tab1Filters.obrano = '';
      this.tab1Filters.productRef = '';
      this.tab1Filters.brand = '';
      this.tab1Filters.dateFrom = null;
      this.tab1Filters.dateTo = null;
      this.fetchClientOrders();
    },
    createNewClientOrder() {
      this.$router.push({ name: 'InternalDossiersFormView' });
    },
    async convertSelectedToSupplierOrder() {
      if (!this.selectedClientLines.length) return;
      this.convertLoading = true;
      try {
        const bostamps = [...new Set(this.selectedClientLines.map(line => line.bostamp))];
        const bistamps = this.selectedClientLines.map(line => line.bistamp);

        const draftData = await dossierService.getMultipleConversionDraft(bostamps, 2, bistamps);
        sessionStorage.setItem('internal_dossier_conversion_draft', JSON.stringify(draftData));

        this.showToast('success', 'Conversão', 'Rascunho de encomenda a fornecedor gerado. Redirecionando...');
        setTimeout(() => {
          this.$router.push({ name: 'InternalDossiersFormView', query: { draft: '1' } });
        }, 800);
      } catch (error) {
        this.showToast('error', 'Erro', error.response?.data?.error || 'Erro ao gerar rascunho de conversão.');
      } finally {
        this.convertLoading = false;
      }
    },
    async convertSelectedToWarehouseTransfer() {
      if (!this.selectedClientLines.length) return;
      const bostamps = this.selectedUniqueBostamps;
      if (bostamps.length !== 1) {
        this.showToast(
          'warn',
          'Transferência de Armazém',
          'A transferência de armazém só é possível para uma única encomenda a cliente.'
        );
        return;
      }

      this.convertLoading = true;
      try {
        const draftData = await dossierService.getMultipleConversionDraft(bostamps, 5);
        sessionStorage.setItem('internal_dossier_conversion_draft', JSON.stringify(draftData));

        this.showToast('success', 'Conversão', 'Rascunho de transferência gerado. Redirecionando...');
        setTimeout(() => {
          this.$router.push({ name: 'InternalDossiersFormView', query: { draft: '1' } });
        }, 800);
      } catch (error) {
        this.showToast('error', 'Erro', error.response?.data?.error || 'Erro ao gerar rascunho de transferência.');
      } finally {
        this.convertLoading = false;
      }
    },

    // ==========================================
    // TAB 2: PEÇAS A RECEBER (ENCOMENDAS A FORNECEDOR)
    // ==========================================
    async fetchSupplierOrders(event = {}) {
      this.loadingTab2 = true;
      this.firstTab2 = event.first ?? 0;
      this.perPageTab2 = event.rows ?? this.perPageTab2;
      try {
        const params = {
          page: this.firstTab2 / this.perPageTab2 + 1,
          per_page: this.perPageTab2,
          obrano: this.tab2Filters.obrano || undefined,
          search: this.tab2Filters.search || undefined,
          date_from: this.tab2Filters.dateFrom ? this.formatDateForStorage(this.tab2Filters.dateFrom) : undefined,
          date_to: this.tab2Filters.dateTo ? this.formatDateForStorage(this.tab2Filters.dateTo) : undefined,
          closed: this.tab2Filters.closed !== null ? (this.tab2Filters.closed ? 1 : 0) : undefined
        };
        const response = await dossierService.getSupplierOrders(params);
        this.supplierOrders = response.data || [];
        this.totalTab2 = response.total;
        this.supplierOrderParams = params;
        this.expandedSupplierRows = [];
        this.summaryStats = response.summary || {
          total_orders: 0,
          total_quantity: 0,
          total_value: 0,
          unique_references: 0
        };
      } catch (error) {
        this.showToast('error', 'Erro', 'Erro ao obter encomendas a fornecedor.');
      } finally {
        this.loadingTab2 = false;
      }
    },
    clearTab2Filters() {
      this.tab2Filters.obrano = '';
      this.tab2Filters.search = '';
      this.tab2Filters.dateFrom = null;
      this.tab2Filters.dateTo = null;
      this.tab2Filters.closed = false;
      this.fetchSupplierOrders();
    },
    async exportToCSV() {
      if (this.exportLoading) return;
      if (!this.supplierOrders.length) {
        this.showToast('info', 'Exportar', 'Não existem dados para exportar.');
        return;
      }

      this.exportLoading = true;
      try {
        const orders = [];
        const params = { ...this.supplierOrderParams, per_page: 50 };
        let lastPage = 1;
        for (let page = 1; page <= lastPage; page++) {
          const response = await dossierService.getSupplierOrders({ ...params, page });
          orders.push(...response.data);
          lastPage = response.last_page;
        }

        const headers = [
          'Nº Encomenda Fornecedor',
          'Fornecedor',
          'Data Encomenda',
          'Estado',
          'Encomenda LG',
          'Referência Peça',
          'Descrição',
          'Quantidade',
          'Preço Unit.',
          'Enc. Cliente Origem',
          'PAT',
          'RNE'
        ];

        const rows = [];
        orders.forEach(order => {
          const lines = order.lines && order.lines.length ? order.lines : [{}];
          lines.forEach(line => {
            rows.push([
              order.id,
              order.name || '',
              this.formatDate(order.date),
              order.closed ? 'Recebido' : 'Por receber',
              order.lg_order_no || '',
              line.product_ref || '',
              line.description || '',
              line.quantity || 0,
              line.cost_price || 0,
              line.client_order_no || '',
              line.pat_no || order.pat_no || '',
              line.service_receipt_no || ''
            ]);
          });
        });

        const csvContent =
          '\uFEFF' +
          [
            headers.join(','),
            ...rows.map(row => row.map(val => `"${String(val ?? '').replace(/"/g, '""')}"`).join(','))
          ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `pecas_a_receber_${this.formatDateForStorage(new Date())}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.showToast('error', 'Erro', 'Erro ao exportar encomendas a fornecedor.');
      } finally {
        this.exportLoading = false;
      }
    },
    editDossier(dossier) {
      this.$router.push({
        name: 'InternalDossiersFormUpdateView',
        params: {
          reference: dossier.reference || dossier.bostamp
        }
      });
    },

    // ==========================================
    // INTEGRATION SELECTOR & LG POP-UP
    // ==========================================
    openIntegrationSelector(order) {
      this.integrationTargetOrder = order;
      const options = [];
      if (order && order.is_lg) {
        options.push({ label: 'LG (GSFS / XLiFT)', value: 'LG' });
      }
      this.availableIntegrations = options.length
        ? options
        : [{ label: 'LG (GSFS / XLiFT)', value: 'LG' }];
      this.selectedIntegration = this.availableIntegrations[0].value;
      this.integrationSelectorVisible = true;
    },
    proceedWithSelectedIntegration() {
      if (!this.selectedIntegration || !this.integrationTargetOrder) return;
      this.integrationSelectorVisible = false;

      if (this.selectedIntegration === 'LG') {
        this.openLgModal(this.integrationTargetOrder);
      }
    },
    backToIntegrationSelector() {
      this.lgModalVisible = false;
      this.integrationSelectorVisible = true;
    },
    async openLgModal(order) {
      this.lgModalVisible = true;
      this.lgPreviewLoading = true;
      this.selectedLgParts = [];
      try {
        const preview = await dossierService.getLgOrderPreview(order.bostamp);
        this.lgModalData = {
          bostamp: preview.bostamp,
          obrano: preview.obrano,
          supplier_name: preview.supplier_name,
          customer_order_no: preview.customer_order_no || String(order.id),
          address_ref_no: preview.address_ref_no || '',
          order_remark: preview.order_remark || '',
          existing_lg_order_no: preview.existing_lg_order_no || null,
          addresses: preview.addresses || [],
          parts: preview.parts || []
        };
        this.selectedLgParts = [...this.lgModalData.parts];
      } catch (error) {
        const msg = error.response?.data?.error || 'Erro ao carregar dados para integração LG.';
        this.showToast('error', 'Integração LG', msg);
        this.lgModalVisible = false;
      } finally {
        this.lgPreviewLoading = false;
      }
    },
    async submitLgPartOrder() {
      if (!this.selectedLgParts.length || this.lgSubmitting) return;
      this.lgSubmitting = true;
      try {
        const payload = {
          bostamp: this.lgModalData.bostamp,
          customer_order_no: this.lgModalData.customer_order_no,
          address_ref_no: this.lgModalData.address_ref_no || '',
          order_remark: this.lgModalData.order_remark || '',
          parts: this.selectedLgParts.map(part => ({
            part_no: part.part_no,
            quantity: Number(part.quantity) || 1,
            delivery_type: part.delivery_type || 'NORMAL',
            service_receipt_no: part.service_receipt_no || '',
            order_line_comment: part.order_line_comment || ''
          }))
        };

        const result = await dossierService.syncLgPart(payload);
        this.showToast('success', 'Integração LG', result.message || 'Encomenda enviada para a LG com sucesso.');

        this.supplierOrders = this.supplierOrders.map(order => {
          if (order.bostamp === result.bostamp) {
            return {
              ...order,
              observation: result.dossier_obs || order.observation,
              lg_order_no: result.part_order_no || order.lg_order_no
            };
          }
          return order;
        });

        this.lgModalVisible = false;
      } catch (error) {
        const msg = error.response?.data?.error || 'Erro ao sincronizar encomenda com a LG.';
        this.showToast('error', 'Integração LG', msg);
      } finally {
        this.lgSubmitting = false;
      }
    },

    // ==========================================
    // UTILITIES
    // ==========================================
    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('pt-PT');
    },
    formatDateForStorage(date) {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
    },
    showToast(severity, summary, detail) {
      this.$toast.add({ severity, summary, detail, life: 4000 });
    }
  }
};
</script>

<style>
.p-datatable .p-datatable-tbody > tr.p-highlight {
    background: #dedede;
    color: #000000;
}

.p-datatable.p-datatable-striped .p-datatable-tbody > tr:nth-child(even).p-highlight {
    background: #dedede;
    color: #000000;
}
</style>

<style scoped>
.kpi-title {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  font-weight: bold;
  display: block;
  margin-bottom: 0.4rem;
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: bold;
}
.warehouse-stock-badge {
  display: inline-block;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-right: 0.3rem;
  margin-bottom: 0.3rem;
  border: 1px solid transparent;
}
.warehouse-stock-badge.stock-ok {
  background-color: #e8f5e9;
  color: #2e7d32;
  border-color: #c8e6c9;
}
.warehouse-stock-badge.stock-empty {
  background-color: #ffebee;
  color: #c62828;
  border-color: #ffcdd2;
}
.no-stock-label {
  color: #ef4444;
  font-weight: bold;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
